import React from 'react';
import PropTypes from 'prop-types';
import { brandColors, interactive, textColors } from '../../tokens';

/**
 * Toggle Component
 * 
 * Switch/toggle component for binary settings (on/off states).
 * Perfect for settings pages and preferences.
 * 
 * @example
 * // Basic toggle
 * <Toggle
 *   checked={emailNotifications}
 *   onChange={(e) => setEmailNotifications(e.target.checked)}
 *   label="Email notifications"
 * />
 * 
 * @example
 * // Toggle with description
 * <Toggle
 *   checked={darkMode}
 *   onChange={(e) => setDarkMode(e.target.checked)}
 *   label="Dark mode"
 *   description="Enable dark theme across the application"
 * />
 */
const Toggle = ({
  // Value and change handlers
  checked = false,
  onChange,
  onBlur,
  onFocus,
  
  // Toggle attributes
  name,
  id,
  disabled = false,
  
  // Label and description
  label = '',
  description = '',
  labelPosition = 'left', // 'left' | 'right'
  
  // Sizing
  size = 'md', // 'sm' | 'md' | 'lg'
  
  // Variant / color
  variant = 'primary', // 'primary' | 'brand' | 'success'

  // Styling
  className = '',
  
  // Accessibility
  'aria-label': ariaLabel,
  'aria-describedby': ariaDescribedBy,
  
  // Additional props
  ...rest
}) => {
  // Auto-generate ID for aria-describedby
  const descriptionId = description && id ? `${id}-description` : undefined;
  
  // Build aria-describedby
  let describedBy = ariaDescribedBy || '';
  if (descriptionId) describedBy = describedBy ? `${describedBy} ${descriptionId}` : descriptionId;
  
  // Size classes for toggle
  const sizeClasses = {
    sm: {
      track: 'w-8 h-5',
      thumb: 'w-4 h-4',
      translate: 'translate-x-3',
    },
    md: {
      track: 'w-11 h-6',
      thumb: 'w-5 h-5',
      translate: 'translate-x-5',
    },
    lg: {
      track: 'w-14 h-7',
      thumb: 'w-6 h-6',
      translate: 'translate-x-7',
    },
  };
  
  // Label text size classes
  const labelSizeClasses = {
    sm: 'text-sm',
    md: 'text-base',
    lg: 'text-lg',
  };
  
  // Description size classes
  const descriptionSizeClasses = {
    sm: 'text-xs',
    md: 'text-sm',
    lg: 'text-base',
  };
  
  // Track classes
  const trackBaseClasses =
    'relative inline-flex items-center flex-shrink-0 rounded-full transition-all duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-white dark:focus:ring-offset-dark-900';
  
  const trackStateClasses = disabled 
    ? 'cursor-not-allowed opacity-50'
    : 'cursor-pointer';
  
  const checkedTrackClassesByVariant = {
    primary: `${interactive.primaryBg} ${interactive.primaryHover}`,
    brand: `${brandColors.primaryBg} ${brandColors.primaryBgHover}`,
    success: 'bg-green-600 dark:bg-green-500 hover:bg-green-700 dark:hover:bg-green-600',
  };

  const focusRingByVariant = {
    primary: 'focus:ring-blue-500 dark:focus:ring-blue-400',
    brand: brandColors.focus,
    success: 'focus:ring-green-500 dark:focus:ring-green-400',
  };

  const trackColorClasses = checked
    ? (checkedTrackClassesByVariant[variant] || checkedTrackClassesByVariant.primary)
    : 'bg-gray-200 dark:bg-dark-700';
  
  const trackClasses = [
    trackBaseClasses,
    focusRingByVariant[variant] || focusRingByVariant.primary,
    trackStateClasses,
    trackColorClasses,
    sizeClasses[size].track,
  ].filter(Boolean).join(' ');
  
  // Thumb classes
  const thumbBaseClasses = 'inline-block bg-white rounded-full shadow-md transform ring-0 transition-transform duration-200 ease-in-out';
  
  const thumbPositionClasses = checked
    ? sizeClasses[size].translate
    : 'translate-x-0.5';
  
  const thumbClasses = [
    thumbBaseClasses,
    thumbPositionClasses,
    sizeClasses[size].thumb,
  ].filter(Boolean).join(' ');
  
  // Label and description container
  const textContent = (label || description) && (
    <div className="flex-1">
      {label && (
        <div className={`font-medium ${textColors.primary} ${labelSizeClasses[size]}`}>
          {label}
        </div>
      )}
      {description && (
        <div 
          id={descriptionId}
          className={`${textColors.tertiary} ${descriptionSizeClasses[size]}`}
        >
          {description}
        </div>
      )}
    </div>
  );
  
  // Toggle element
  const toggleElement = (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={ariaLabel || label || 'Toggle'}
      aria-describedby={describedBy || undefined}
      onClick={(e) => {
        if (!disabled && onChange) {
          // Simulate checkbox change event
          onChange({ target: { checked: !checked, name } });
        }
      }}
      onBlur={onBlur}
      onFocus={onFocus}
      disabled={disabled}
      className={trackClasses}
      {...rest}
    >
      <span className="sr-only">{label || 'Toggle'}</span>
      <span className={thumbClasses} />
    </button>
  );
  
  // Hidden checkbox for form submission
  const hiddenCheckbox = (
    <input
      type="checkbox"
      name={name}
      id={id}
      checked={checked}
      onChange={() => {}} // Controlled by button
      className="sr-only"
      tabIndex={-1}
      aria-hidden="true"
    />
  );
  
  return (
    <label 
      className={`inline-flex items-center justify-between gap-4 ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'} ${className}`}
    >
      {labelPosition === 'left' && textContent}
      <div className="flex items-center">
        {toggleElement}
        {hiddenCheckbox}
      </div>
      {labelPosition === 'right' && textContent}
    </label>
  );
};

Toggle.propTypes = {
  checked: PropTypes.bool,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  onFocus: PropTypes.func,
  
  name: PropTypes.string,
  id: PropTypes.string,
  disabled: PropTypes.bool,
  
  label: PropTypes.string,
  description: PropTypes.string,
  labelPosition: PropTypes.oneOf(['left', 'right']),
  
  size: PropTypes.oneOf(['sm', 'md', 'lg']),

  variant: PropTypes.oneOf(['primary', 'brand', 'success']),
  
  className: PropTypes.string,
  
  'aria-label': PropTypes.string,
  'aria-describedby': PropTypes.string,
};

export default Toggle;
